import { z } from 'zod';

/**
 * What the Mini Planner card says a hotel, experience or restaurant is —
 * facts the rest of the product does not hold. Stored as one jsonb bag on
 * the product (`product.card_facts`); every field is optional and the card
 * shows a row only when it is set.
 */

export const EXPERIENCE_FORMATS = ['Private', 'Shared', 'Small group'] as const;

export const RESTAURANT_FORMATS = [
  'Fine dining',
  'Casual',
  'Tapas',
  'Seafood',
  'Street food',
  'Comfort food',
] as const;

// The same three words the request (brief) offers for its pace.
export const PACES = ['Relaxed', 'Balanced', 'Active'] as const;
export type CardPace = (typeof PACES)[number];

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const CARD_LANGUAGES = [
  'English',
  'Portuguese',
  'Spanish',
  'French',
  'German',
  'Italian',
  'Dutch',
  'Russian',
  'Polish',
  'Swedish',
  'Hebrew',
  'Arabic',
  'Chinese',
  'Japanese',
] as const;

const count = z.number().int().min(1).max(10_000);

export const cardFactsSchema = z.object({
  /** Hotels: how many rooms. */
  rooms: count.nullable().optional(),
  /** Experiences: Private / Shared / Small group; restaurants: their kind. */
  format: z
    .enum([...EXPERIENCE_FORMATS, ...RESTAURANT_FORMATS])
    .nullable()
    .optional(),
  /** Experiences: the languages it runs in. */
  languages: z
    .array(z.enum(CARD_LANGUAGES))
    .max(CARD_LANGUAGES.length)
    .optional(),
  /** Experiences: how demanding it is. */
  pace: z.enum(PACES).nullable().optional(),
  /** Experiences: the most guests a group-priced one takes. */
  capacity: count.nullable().optional(),
  /** Restaurants: the days it is closed. */
  closedDays: z.array(z.enum(WEEKDAYS)).max(7).optional(),
  /** Restaurants: seats in the private room; unset when it has none. */
  privateRoomSeats: count.nullable().optional(),
});
export type CardFacts = z.infer<typeof cardFactsSchema>;

/** A stored bag as its valid facts; null when there are none. */
export const parseCardFacts = (raw: unknown): CardFacts | null => {
  if (raw == null) return null;
  const parsed = cardFactsSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};
